import PropTypes from 'prop-types';
import React from 'react';
import {
  R29_IMAGE_REGEX,
  NEW_IMAGE_REGEX,
  JPG_QUALITY,
  JPG_QUALITY_HQ,
  RESPONSIVE_IMG_WIDTH_PX,
  RESPONSIVE_IMG_HEIGHT_PX
} from '../../constants';
import {
  shouldRenderResponsiveImg,
  shouldForceImgAsJpg,
  shouldForceImgAsWebP,
  getSafeDimensionsForOpener,
  getSafeDimensionsForSection
} from '../../utils/images';
import FastlyResponsiveImg from './fastly_responsive_img';
import renderClientSideOnly from '../hocs/renderClientSideOnly';
import {omit} from 'lodash';

const fallbackImg = ({ src, alt = '', ...delegatedProps }) =>
  <img src={src} alt={alt} {...delegatedProps} />;

fallbackImg.propTypes = {
  src: PropTypes.string
};

/*
 * ResponsiveImg never renders server-side. This is so that we can
 * change the extension for certain browsers, without triggering
 * a double image request.
 *
 * We accomplish this by decorating ResponsiveImg with the
 * `renderClientSideOnly` HOC -- this happens at the bottom of this file.
 */

export const ResponsiveImg = (props, context) => {
  const { location } = context;

  if (typeof window === 'undefined') {
    throw new Error(
      'Tried to render a ResponsiveImg on the server-side.'
      + ' This should never happen.'
    );
  }

  const { src, width, height, original_width_px, original_height_px,
    format, optimizeOrientation, alt = '' } = props;
  const delegatedProps = omit(props, [
    'src', 'width', 'height', 'original_width_px', 'original_height_px',
    'format', 'optimizeOrientation', 'alt'
  ]);

  if (!src) {
    return fallbackImg({ src, alt, ...delegatedProps });
  }

  const pieces = src.match(R29_IMAGE_REGEX);
  if (!pieces) {
    if (src.match(NEW_IMAGE_REGEX)) {
      return <FastlyResponsiveImg {...props} />;
    }

    return fallbackImg({ src, ...delegatedProps });
  }
  const checksum = pieces[2];
  const prefix =
    pieces[1] // e.g. "http://s1.r29static.com//bin/entry/5ab/0,0,2000,2400/"
    + checksum // e.g. "5ab"
    + pieces[3]; // e.g. "/0,0,2000,2400/"
  const infix = pieces[5]; // e.g. "/1774547/image-name-1774547."

  const srcExtension = pieces[6];
  const forceImgAsWebP = shouldForceImgAsWebP(location, srcExtension);


  const forceImgAsJpg = shouldForceImgAsJpg(srcExtension, location);
  const extension = determineFinalExtension(srcExtension, format, forceImgAsJpg, forceImgAsWebP);
  const qualityString = determineQualityString(extension, format, forceImgAsJpg, forceImgAsWebP);
  if (!shouldRenderResponsiveImg(extension)) {
    return fallbackImg({ src, alt, ...delegatedProps });
  }

  /*
   * Fallback for images for which we want to just render the full-sized asset,
   * like when it's a full-bleed image on desktop.
   */
  if (!width || !height) {
    // Question: Why do we not set srcset here?
    return (
      <img
        src={prefix + 'x' + qualityString + infix + extension}
        alt={alt}
        {...delegatedProps}
      />
    );
  }

  if (optimizeOrientation) {
    const dimension = width > height ? `${2 * RESPONSIVE_IMG_WIDTH_PX}x` : `x${2 * RESPONSIVE_IMG_HEIGHT_PX}`;
    // Question: Why do we not set srcset here?
    return (
      <img src={prefix + dimension + qualityString + infix + extension}
        alt={alt}
        {...delegatedProps}
      />
    );
  }

  return (
    <img src={getSrc(
      width, height, prefix, qualityString, infix, extension,
      original_width_px, original_height_px
    )} srcSet={getSrcSet(
      width, height, prefix, qualityString, infix, extension,
      original_width_px, original_height_px
    )} alt={alt} {...delegatedProps}
    />
  );
};

export function determineFinalExtension(srcExtension, format, forceImgAsJpg, forceImgAsWebP) {
  if (forceImgAsWebP) {
    return 'webp';
  }
  switch (format) {
    case 'alwaysForceJpgHq':
      return 'jpg';
    case 'alwaysUseSrcExtension':
      return srcExtension;
    default:
      return forceImgAsJpg ? 'jpg' : srcExtension;
  }
}

function determineQualityString(finalExtension, format, forceImgAsJpg, forceImgAsWebP) {
  if (!/jpe?g/i.test(finalExtension) && !forceImgAsWebP) {
    return '';
  }

  if (format === 'alwaysForceJpgHq' || forceImgAsJpg || forceImgAsWebP) {
    return ',' + JPG_QUALITY_HQ;
  }

  return ',' + JPG_QUALITY;
}

function getSrc(
  width, height, prefix, qualityString, infix, extension,
  original_width_px, original_height_px
) {
  const { safeWidth, safeHeight } = original_width_px && original_height_px
    ? getSafeDimensionsForSection(
      width, height, original_width_px, original_height_px
    )
    : getSafeDimensionsForOpener(width, height);
  return prefix +
    Math.round(safeWidth) + 'x' + Math.round(safeHeight) + qualityString
    + infix + extension;
}

function getSrcSet(
  width, height, prefix, qualityString, infix, extension,
  original_width_px, original_height_px
) {
  const { safeWidth, safeHeight } = original_width_px && original_height_px
    ? getSafeDimensionsForSection(
      width * 2, height * 2, original_width_px, original_height_px
    )
    : getSafeDimensionsForOpener(width * 2, height * 2);
  return prefix + Math.round(safeWidth) + 'x' + Math.round(safeHeight)
    + qualityString + infix + extension + ' 2x';
}

ResponsiveImg.propTypes = {
  /*
   * Props passed in, but not named here, will be forwarded
   * to the <img> component
   */

  width: PropTypes.number,
  height: PropTypes.number,
  src: PropTypes.string,
  original_width_px: PropTypes.number,
  original_height_px: PropTypes.number,
  format: PropTypes.oneOf([
    'alwaysForceJpgHq',
    'alwaysUseSrcExtension'
  ]),
  optimizeOrientation: PropTypes.bool
};

ResponsiveImg.defaultProps = {
  // "alt" must be explicitly set here b/c screenreaders will read the src tag if an alt tag is not present
  alt: ''
};

ResponsiveImg.contextTypes = {
  location: PropTypes.object.isRequired
};

export default renderClientSideOnly()(ResponsiveImg);
